import type { DocumentWithTimestamps, Timestamp } from './firebase.ts'
import type { UserId } from './ids.ts'

export type ChatId = string

export type MessageId = string

export type ChatroomId = string

export type Message = {
    id: MessageId
    text: string
    attachments?: any[]
    createdAt: Timestamp
    createdById: UserId
    mentionedUserIds?: UserId[]
    isAutoReply?: boolean
}

export type LastMessage = Message & {
    seenByAt: { [UserId: string]: Timestamp }
}

export enum ChatTopicBase {
    mentoring = 'mentoring',
    participationRequest = 'participationRequest',
    jobRequest = 'jobRequest',
    project = 'project',
    skill = 'skill',
    community = 'community', // Grouped community chat
    communityRequest = 'communityRequest' // One-on-one chat for community members
}

// Chat types for the new platform-wide chat system
export enum ChatType {
    direct = 'direct', // 1:1 chat between two users
    group = 'group', // Private group chat (invite only)
    chatroom = 'chatroom' // Public chatroom anyone can join
}

// Mentoring chat closure status
export enum MentoringClosureStatus {
    success = 'success', // Mentoring completed successfully
    partial = 'partial', // Partially successful / ongoing
    cancelled = 'cancelled' // Cancelled or not completed
}

// Mentoring closure data
export interface MentoringClosure {
    closedAt: Timestamp
    closedById: UserId
    status: MentoringClosureStatus
    feedback?: string // Optional feedback text
    mentorRating?: number // 1-5 star rating for mentor
    menteeRating?: number // 1-5 star rating for mentee (from mentor's perspective)
}

// Predefined chatroom topics
export enum ChatroomTopic {
    general = 'general',
    mentoring = 'mentoring',
    volunteering = 'volunteering',
    education = 'education',
    careers = 'careers',
    selfKnowledge = 'selfKnowledge',
    jobs = 'jobs',
    internships = 'internships',
    random = 'random'
}

export type Chat = DocumentWithTimestamps & {
    id: ChatId
    memberIds: UserId[]
    title?: string
    topic?: ChatTopicBase | string
    image?: string
    firstMessageById?: UserId
    lastMessages?: {
        [UserId: string]: LastMessage
    }
    // New fields for platform-wide chat
    type?: ChatType
    chatroomTopic?: ChatroomTopic
    description?: string
    isPublic?: boolean
    communityId?: string | null // For community-specific chats
    // Mentoring closure
    mentoringClosure?: MentoringClosure
}

export type ChatData = Pick<Chat, 'memberIds' | 'title' | 'topic' | 'image'>

export interface MessagesGroup {
    id: string
    userId: UserId
    messages: Message[]
}

// Presence tracking
export type PresenceStatus = 'online' | 'away' | 'busy' | 'dnd'

export interface UserPresence {
    online: boolean
    lastSeen: Timestamp
    status: PresenceStatus
    isManual?: boolean // true if user manually set status (don't auto-change)
}

// Chatroom definition (public rooms)
export interface Chatroom {
    id: ChatroomId
    name: string
    slug: string // e.g. "mentoring", "random" for #mentoring, #random
    topic: ChatroomTopic
    description: string
    icon?: string
    memberCount: number
    chatId: ChatId // Reference to the actual chat
    createdAt: Timestamp
    createdById: UserId
    // Channel settings
    isDefault?: boolean // Auto-join all new users
    isSystem?: boolean // Cannot be deleted
    autoJoinRoles?: string[] // Auto-join users with these roles (mentor, volunteer, etc.)
    sortOrder?: number // Display order in channel list
    communityId?: string // For community-specific channels (null = platform-wide)
}

// Channel member with presence
export interface ChannelMember {
    id: UserId
    joinedAt: Timestamp
    lastSeenAt?: Timestamp
}
